import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useRegistrarIngresoFondo, useRegistrarEgresoFondo } from './api';
import { Plus } from 'lucide-react';

interface RegistrarMovimientoFormProps {
  condoId: string;
}

type TipoMovimiento = 'ingreso' | 'egreso';

const CATEGORIAS_INGRESO = [
  { value: 'aporte_extraordinario', label: 'Aporte extraordinario' },
  { value: 'gasto_autorizado', label: 'Gasto autorizado' },
] as const;

const CATEGORIAS_EGRESO = [
  { value: 'gasto_autorizado', label: 'Gasto autorizado' },
] as const;

export function RegistrarMovimientoForm({ condoId }: RegistrarMovimientoFormProps) {
  const [tipo, setTipo] = useState<TipoMovimiento>('ingreso');
  const [monto, setMonto] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [categoria, setCategoria] = useState('aporte_extraordinario');

  const ingresoMutation = useRegistrarIngresoFondo(condoId);
  const egresoMutation = useRegistrarEgresoFondo(condoId);

  const isPending = ingresoMutation.isPending || egresoMutation.isPending;
  const error = ingresoMutation.error || egresoMutation.error;

  const categorias = tipo === 'ingreso' ? CATEGORIAS_INGRESO : CATEGORIAS_EGRESO;

  function handleTipoChange(newTipo: TipoMovimiento) {
    setTipo(newTipo);
    // Reset to first valid category when switching type
    setCategoria(newTipo === 'ingreso' ? 'aporte_extraordinario' : 'gasto_autorizado');
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const montoNum = parseInt(monto, 10);
    if (!montoNum || montoNum <= 0 || !descripcion.trim()) return;

    const onSuccess = () => {
      setMonto('');
      setDescripcion('');
    };

    if (tipo === 'ingreso') {
      ingresoMutation.mutate(
        { monto: montoNum, descripcion: descripcion.trim(), categoria: categoria as 'aporte_extraordinario' | 'gasto_autorizado' },
        { onSuccess },
      );
    } else {
      egresoMutation.mutate(
        { monto: montoNum, descripcion: descripcion.trim() },
        { onSuccess },
      );
    }
  }

  const isValid = parseInt(monto, 10) > 0 && descripcion.trim().length >= 3;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Registrar movimiento</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Tipo */}
          <div className="space-y-2">
            <Label>Tipo de movimiento</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={tipo === 'ingreso' ? 'primary' : 'outline'}
                size="sm"
                onClick={() => handleTipoChange('ingreso')}
              >
                Ingreso
              </Button>
              <Button
                type="button"
                variant={tipo === 'egreso' ? 'danger' : 'outline'}
                size="sm"
                onClick={() => handleTipoChange('egreso')}
              >
                Egreso
              </Button>
            </div>
          </div>

          {/* Categoria */}
          <div className="space-y-2">
            <Label htmlFor="categoria">Categoria</Label>
            <Select value={categoria} onValueChange={setCategoria}>
              <SelectTrigger id="categoria">
                <SelectValue placeholder="Seleccionar categoria" />
              </SelectTrigger>
              <SelectContent>
                {categorias.map((c) => (
                  <SelectItem key={c.value} value={c.value}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Monto */}
          <div className="space-y-2">
            <Label htmlFor="monto">Monto (CLP)</Label>
            <Input
              id="monto"
              type="number"
              min={1}
              placeholder="Ej: 500000"
              value={monto}
              onChange={(e) => setMonto(e.target.value)}
            />
          </div>

          {/* Descripcion */}
          <div className="space-y-2">
            <Label htmlFor="descripcion">Descripcion</Label>
            <textarea
              id="descripcion"
              rows={3}
              placeholder="Describe el motivo del movimiento (min. 3 caracteres)"
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              className="flex w-full rounded border border-[var(--color-hairline-strong)] bg-[var(--color-paper)] px-3 py-2 text-sm text-[var(--color-ink)] placeholder:text-[var(--color-ink-faint)] shadow-[inset_0_1px_0_rgba(0,0,0,0.02)] transition-[border-color,box-shadow] duration-150 ease-out disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-none focus-visible:border-[var(--color-primary)] focus-visible:shadow-[0_0_0_3px_color-mix(in_oklch,var(--color-primary)_20%,transparent)]"
            />
          </div>

          {error && (
            <p className="text-sm text-[var(--color-danger)]">
              Error al registrar el movimiento. Intenta nuevamente.
            </p>
          )}

          <Button type="submit" disabled={!isValid || isPending}>
            <Plus className="h-4 w-4" />
            {isPending ? 'Registrando...' : 'Registrar movimiento'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
